import { Badge } from '@/components/ui/badge';
import { MessageSquare, Layers, FileText, Gauge, Sparkles } from 'lucide-react';
import { useValueAssessmentTelemetry } from '@/hooks/useValueAssessmentTelemetry';

export type DiscoveryMethod = 'chat' | 'card-sort' | 'reflection' | 'assessment';

interface DiscoveryMethodSelectorProps {
  onSelect: (method: DiscoveryMethod) => void;
}

const METHODS = [
  {
    id: 'chat' as const,
    icon: MessageSquare,
    title: 'AI Conversation',
    description: 'Guided dialogue to explore what matters to you',
    duration: '~5 min',
    recommended: true,
  },
  {
    id: 'card-sort' as const,
    icon: Layers,
    title: 'Values Card Sort',
    description: 'Sort 30 values into importance categories',
    duration: '~8 min',
    recommended: false,
  },
  {
    id: 'reflection' as const,
    icon: FileText,
    title: 'Reflection Prompts',
    description: 'Written exercises about meaningful experiences',
    duration: '~12 min',
    recommended: false,
  },
  {
    id: 'assessment' as const,
    icon: Gauge,
    title: 'Quick Assessment',
    description: 'Rate 15 statements for instant results',
    duration: '~2 min',
    recommended: false,
  },
];

export const DiscoveryMethodSelector = ({ onSelect }: DiscoveryMethodSelectorProps) => {
  const { trackMethodSelected } = useValueAssessmentTelemetry();
  
  const handleSelect = (method: DiscoveryMethod) => {
    // Map UI method names to telemetry method names
    const methodMap: Record<DiscoveryMethod, 'ai_conversation' | 'values_card_sort' | 'reflection_prompts' | 'quick_assessment'> = {
      'chat': 'ai_conversation',
      'card-sort': 'values_card_sort',
      'reflection': 'reflection_prompts',
      'assessment': 'quick_assessment',
    };
    trackMethodSelected(methodMap[method]);
    onSelect(method);
  };

  return (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <h3 className="font-semibold text-lg">Choose Your Discovery Method</h3>
        <p className="text-sm text-muted-foreground">
          Select the approach that feels right for you
        </p>
      </div>

      <div className="grid gap-3">
        {METHODS.map((method) => {
          const Icon = method.icon;
          return (
            <button
              key={method.id}
              onClick={() => handleSelect(method.id)}
              className={`flex items-start gap-4 p-4 rounded-lg border transition-all text-left group ${
                method.recommended
                  ? 'border-primary/50 bg-primary/5 hover:bg-primary/10 ring-1 ring-primary/20'
                  : 'border-border bg-card hover:border-primary/50 hover:bg-accent/50'
              }`}
            >
              <div className={`p-2 rounded-lg transition-colors ${
                method.recommended
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground'
              }`}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{method.title}</span>
                    {method.recommended && (
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 bg-primary/15 text-primary border-0">
                        <Sparkles className="w-2.5 h-2.5 mr-0.5" />
                        Recommended
                      </Badge>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground">{method.duration}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {method.description}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};